/**
 * useBreakpoints Hook
 *
 * Provides standardized responsive breakpoint detection.
 *
 * Breakpoints:
 * - mobile: < 640px
 * - tablet: 640px - 1023px
 * - desktop: 1024px - 1439px
 * - widescreen: >= 1440px
 */

import { useState, useEffect, useCallback } from 'react';

export const BREAKPOINTS = {
  mobile: 640,
  tablet: 1024,
  desktop: 1440,
} as const;

export type BreakpointName = 'mobile' | 'tablet' | 'desktop' | 'widescreen';

export interface UseBreakpointsResult {
  width: number;
  breakpoint: BreakpointName;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isWidescreen: boolean;
  isTabletOrSmaller: boolean;
  isDesktopOrLarger: boolean;
}

function getBreakpointName(width: number): BreakpointName {
  if (width < BREAKPOINTS.mobile) return 'mobile';
  if (width < BREAKPOINTS.tablet) return 'tablet';
  if (width < BREAKPOINTS.desktop) return 'desktop';
  return 'widescreen';
}

export function useBreakpoints(): UseBreakpointsResult {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : BREAKPOINTS.desktop
  );

  const handleResize = useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    handleResize();

    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [handleResize]);

  const breakpoint = getBreakpointName(width);
  const isMobile = width < BREAKPOINTS.mobile;
  const isTablet = width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet;
  const isDesktop = width >= BREAKPOINTS.tablet && width < BREAKPOINTS.desktop;
  const isWidescreen = width >= BREAKPOINTS.desktop;

  return {
    width,
    breakpoint,
    isMobile,
    isTablet,
    isDesktop,
    isWidescreen,
    isTabletOrSmaller: isMobile || isTablet,
    isDesktopOrLarger: isDesktop || isWidescreen,
  };
}
